import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Search, 
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Save
} from "lucide-react";

const API = "/api";

export default function FiyatDegistirPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [urunler, setUrunler] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUrun, setSelectedUrun] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Fiyat formu
  const [fiyatlar, setFiyatlar] = useState({
    satinAlma: "",
    toptanSatis: "",
    ilkSatis: "",
    perakendeSatis: ""
  });

  useEffect(() => {
    fetchUrunler();
  }, [token, page, searchTerm]);

  const fetchUrunler = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/nebim/products?token=${token}&page=${page}&limit=20&search=${searchTerm}`);
      if (response.data.success) {
        const products = response.data.products || [];
        const grouped = {};
        products.forEach(p => {
          if (!grouped[p.ProductCode]) {
            grouped[p.ProductCode] = {
              ...p,
              renkler: []
            };
          }
          if (p.ColorCode && !grouped[p.ProductCode].renkler.find(r => r.kod === p.ColorCode)) {
            grouped[p.ProductCode].renkler.push({
              kod: p.ColorCode,
              ad: p.ColorDescription || p.ColorCode
            });
          }
        });
        setUrunler(Object.values(grouped));
        setHasMore(products.length === 20);
      }
    } catch (error) {
      console.error("Ürünler alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUrunSec = async (urun) => {
    setSelectedUrun(urun);
    // Mevcut fiyatları getir
    try {
      const response = await axios.get(`${API}/nebim/urun-fiyat?token=${token}&urun_kodu=${urun.ProductCode}`);
      if (response.data.success) {
        setFiyatlar(response.data.fiyatlar || {
          satinAlma: urun.PurchasePrice || "",
          toptanSatis: urun.WholesalePrice || "",
          ilkSatis: urun.FirstSalePrice || "",
          perakendeSatis: urun.SalesPrice || ""
        });
      }
    } catch (error) {
      setFiyatlar({
        satinAlma: urun.PurchasePrice || "",
        toptanSatis: "",
        ilkSatis: "",
        perakendeSatis: urun.SalesPrice || ""
      });
    }
  };

  const handleKaydet = async () => {
    if (!selectedUrun) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/nebim/fiyat-guncelle?token=${token}`, {
        urun_kodu: selectedUrun.ProductCode,
        fiyatlar
      });

      if (response.data.success) {
        toast.success("Fiyatlar güncellendi");
      } else {
        toast.error(response.data.error || "Güncelleme başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Güncelleme hatası");
    } finally {
      setLoading(false);
    }
  };

  // Detay view
  if (selectedUrun) {
    return (
      <div className="space-y-6 fade-in" data-testid="fiyat-degistir-detay">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setSelectedUrun(null)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Fiyat Değiştir</h1>
            <p className="text-slate-500 text-sm">{selectedUrun.ProductCode} - {selectedUrun.ProductName}</p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-6">
            <h3 className="font-semibold text-lg">Fiyat Bilgileri</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Satın Alma Fiyatı</Label>
                <div className="flex gap-2">
                  <div className="w-20 h-10 flex items-center justify-center bg-slate-100 rounded-md text-sm font-medium">
                    TRY
                  </div>
                  <Input
                    type="number"
                    value={fiyatlar.satinAlma}
                    onChange={(e) => setFiyatlar({...fiyatlar, satinAlma: e.target.value})}
                    placeholder="0.00"
                    data-testid="input-satin-alma"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Toptan Satış Fiyatı</Label>
                <div className="flex gap-2">
                  <div className="w-20 h-10 flex items-center justify-center bg-slate-100 rounded-md text-sm font-medium">
                    TRY
                  </div>
                  <Input
                    type="number"
                    value={fiyatlar.toptanSatis}
                    onChange={(e) => setFiyatlar({...fiyatlar, toptanSatis: e.target.value})}
                    placeholder="0.00"
                    data-testid="input-toptan-satis"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>İlk Satış Fiyatı</Label>
                <div className="flex gap-2">
                  <div className="w-20 h-10 flex items-center justify-center bg-slate-100 rounded-md text-sm font-medium">
                    TRY
                  </div>
                  <Input
                    type="number"
                    value={fiyatlar.ilkSatis}
                    onChange={(e) => setFiyatlar({...fiyatlar, ilkSatis: e.target.value})}
                    placeholder="0.00"
                    data-testid="input-ilk-satis"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Perakende Satış Fiyatı</Label>
                <div className="flex gap-2">
                  <div className="w-20 h-10 flex items-center justify-center bg-slate-100 rounded-md text-sm font-medium">
                    TRY
                  </div>
                  <Input
                    type="number"
                    value={fiyatlar.perakendeSatis}
                    onChange={(e) => setFiyatlar({...fiyatlar, perakendeSatis: e.target.value})}
                    placeholder="0.00"
                    data-testid="input-perakende"
                  />
                </div>
              </div>
            </div>

            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
              onClick={handleKaydet}
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Liste view
  return (
    <div className="space-y-6 fade-in" data-testid="fiyat-degistir-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fiyat Değiştir</h1>
          <p className="text-slate-500 text-sm">Ürün fiyatlarını güncelleyin</p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          placeholder="Ürün ara..."
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {urunler.map((urun, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 hover:bg-slate-50">
                <div className="flex-1">
                  <p className="font-medium">{urun.ProductCode}</p>
                  <p className="text-sm text-slate-500">{urun.ProductName}</p>
                  <div className="flex gap-1 mt-1">
                    {urun.renkler.slice(0, 5).map((renk, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {renk.ad}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="text-right mr-4">
                  <p className="font-semibold text-emerald-600">{urun.SalesPrice?.toFixed(2) || "0.00"} TL</p>
                  <p className="text-xs text-slate-400">Perakende</p>
                </div>
                <Button 
                  onClick={() => handleUrunSec(urun)}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Fiyat Değiştir
                </Button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between p-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Önceki
            </Button>
            <span className="text-sm text-slate-500">Sayfa {page}</span>
            <Button 
              variant="outline" 
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
            >
              Sonraki
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
